import { Box, Icon, Spinner, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import useGetIncidencesPayrollIntegrationStatus from 'modules/bookkeepers/hooks/useGetIncidencePayrollIntegrationStatus'
import type { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { trackLoadIncidenceTable as trackLoadIncidencesTable } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'

import Item from './Item'
import Row from './Row'

interface Props {
  incidences: IncidenceQueryData[]
  loading: boolean
  selectedIncidence: string | null | undefined
  onRowSelectPath: (incidenceId: string, isReopened: boolean) => string
  colSpan: number
  showIncidenceTypeColumn: boolean
  showIncidencePayrollIntegrationStatus: boolean
  selectableIncidenceIds?: Set<string>
  selectedIncidencesId?: Set<string>
  onToggleIncidence?: (id: string) => void
}

const List = ({
  incidences,
  loading,
  selectedIncidence,
  onRowSelectPath,
  colSpan,
  showIncidenceTypeColumn,
  showIncidencePayrollIntegrationStatus,
  selectableIncidenceIds,
  selectedIncidencesId,
  onToggleIncidence,
}: Props) => {
  const isBookkeeperView = useBookkeeperView()
  const { hasInhousePayroll } = useInhousePayroll()
  useEffect(() => {
    trackLoadIncidencesTable(incidences.length, isBookkeeperView, hasInhousePayroll)
  }, [incidences.length, isBookkeeperView, hasInhousePayroll])
  const incidenceIds = incidences.map((item) => item.id)

  const { company, access } = useCurrentSession()
  const { selectedCompany } = useIncidenceFilterContext()

  /**
   * FIXME: This is a hack because there is not always a current company. The
   * backend needs to become more flexible to support multiple company IDs.
   */
  const companyIdForIncidenceStatuses = !isBookkeeperView ? company.id : (selectedCompany?.id ?? '0')
  const { data: payrollIntegrationStatus } = useGetIncidencesPayrollIntegrationStatus({
    variables: {
      currentUserId: access.userId,
      incidenceIds: incidenceIds,
      companyId: companyIdForIncidenceStatuses,
    },
  })

  if (!incidences.length) {
    return (
      <Table.Row background="grey000White">
        <Table.Cell colSpan={colSpan} padding="s0">
          <Box alignItems="center" height="s300" justifyContent="center">
            <Box justifyContent="center" alignItems="center" paddingY="s8" gap="s12">
              <Icon.Medium icon="clock" color="grey800" />
              <Text.Normal size="300">
                {i18n.t('bookkeepers.incidence.table.no_incidences_found')}
              </Text.Normal>
            </Box>
          </Box>
        </Table.Cell>
      </Table.Row>
    )
  }

  return (
    <>
      {incidences.map((incidence) => {
        const isOpen = selectedIncidence === incidence.id
        const isSelected = selectedIncidencesId?.has(incidence.id) ?? false
        const isSelectable = selectableIncidenceIds?.has(incidence.id) ?? false
        const payrollIncidenceStatus = payrollIntegrationStatus.find(
          (item) => item.incidenceId == incidence.id
        )

        return (
          <Row
            key={incidence.id}
            selected={isOpen}
            background="grey800"
            to={onRowSelectPath(incidence.id, incidence.isReopened)}
          >
            <Item
              isOpen={isOpen}
              isSelected={isSelected}
              isSelectable={isSelectable}
              onToggleSelect={onToggleIncidence}
              showCheckbox={!!onToggleIncidence}
              incidence={incidence}
              payrollIntegrationStatus={payrollIncidenceStatus}
              showIncidenceTypeColumn={showIncidenceTypeColumn}
              showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus}
            />
          </Row>
        )
      })}
      {loading && (
        <Table.Row>
          <Table.Cell colSpan={colSpan}>
            <Box alignItems="center">
              <Spinner />
            </Box>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  )
}

export default observer(List)
